import type {
    InboxWorkflowItem,
    InboxWorkflowRowOutcomeMap,
    InboxWorkflowState,
    InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
import { formatPropertyCustomerRole } from "../../../../../../../../../../../_shared/_logic/customer-roles"
import { formatInboxSourceLabel } from "../../../../../_shared/_logic/cells-presentation"
import {
  sourceCleanupInstruction,
  type SourceCleanupInstructionPresentation,
} from "../../../../../_shared/_logic/source-cleanup-instruction-tags"
import {
  mutationContactPointsForTag,
  mutationContactValue,
  normalizeWrongRecipientReplacementOutput,
  type NormalizedWrongRecipientReplacementOutput,
} from "../../../_shared/_logic/replacement-output"
import { resolveCustomerStatement } from "../../../_shared/_logic/customer-statement"
import type { WrongRecipientSourceCleanupRow } from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {}

const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const stepOutput = (item: InboxWorkflowItem, stepKey: string): JsonObject =>
  objectValue(item.steps.find((entry) => entry.step_key === stepKey)?.output)

const relevantItem = (
  item: InboxWorkflowItem,
  workflow: InboxWorkflowState,
  step: InboxWorkflowStep,
) =>
  item.action_case.flow_key === workflow.flow.flow_key &&
  (item.steps.some((entry) => entry.step_key === step.step_key) ||
    step.requirements_resolved_by_step.some((requirement) =>
      item.action_case.requirements.includes(requirement),
    ))

const itemNeedsStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) =>
  step.requirements_resolved_by_step.some(
    (requirement) =>
      item.action_case.requirements.includes(requirement) &&
      !item.action_case.resolved_requirements.includes(requirement),
  )

const itemResolvedStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = item.steps.find((entry) => entry.step_key === step.step_key)

  if (projected?.status === "resolved") return true

  if (projected?.status === "skipped") return false

  const requirements = step.requirements_resolved_by_step.filter((requirement) =>
    item.action_case.requirements.includes(requirement),
  )

  return (
    requirements.length > 0 &&
    requirements.every((requirement) =>
      item.action_case.resolved_requirements.includes(requirement),
    )
  )
}

const sourceInstructions = (
  output: NormalizedWrongRecipientReplacementOutput,
): SourceCleanupInstructionPresentation[] => {
  const affected = output.affectedCustomer

  if (output.action === "remove") {
    return [
      sourceCleanupInstruction(
        affected.displayName,
        "Falsche Zuordnung entfernen",
        affected.displayName,
      ),
    ]
  }

  const replacement = output.replacementCustomer

  if (!replacement) return []

  const instructions: SourceCleanupInstructionPresentation[] = [
    sourceCleanupInstruction(
      `${affected.displayName} → ${replacement.displayName}`,
      "Ansprechpartner ersetzen",
      replacement.displayName,
    ),
  ]

  if (replacement.role) {
    const role = formatPropertyCustomerRole(replacement.role)
    instructions.push(sourceCleanupInstruction(role, "Rolle setzen", role))
  }

  const replacementEmail = mutationContactValue(replacement, ["EMAIL"])
  const replacementPhone = mutationContactValue(replacement, ["MOBILE", "FIXED_LINE"])

  if (replacementEmail)
    instructions.push(
      sourceCleanupInstruction(replacementEmail, "E-Mail übernehmen", replacementEmail),
    )

  if (replacementPhone)
    instructions.push(
      sourceCleanupInstruction(replacementPhone, "Telefon übernehmen", replacementPhone),
    )

  return instructions
}

const priorOutputError = "Gespeicherte Empfänger-Entscheidung fehlt oder ist ungültig."
const cleanupOutputError = "Gespeicherter Abschluss des Quellenabgleichs fehlt oder ist ungültig."

export const buildWrongRecipientSourceCleanupRows = ({
  workflow,
  step,
  rowOutcomes,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  rowOutcomes: InboxWorkflowRowOutcomeMap
}): WrongRecipientSourceCleanupRow[] =>
  workflow.items
    .filter((item) => relevantItem(item, workflow, step))
    .map((item) => {
      const actionCaseId = item.action_case.id
      const payload = objectValue(item.action_case.payload)
      const property = objectValue(payload.property)
      const address = objectValue(property.address)
      const unit = objectValue(property.unit)
      const wrongRecipient = objectValue(payload.wrong_recipient)
      const presentation = objectValue(payload.presentation)
      const origin = objectValue(payload.origin)
      const replacementOutput = stepOutput(item, "replace_wrong_recipient")
      const cleanupOutput = stepOutput(item, "source_cleanup")
      const replacement = normalizeWrongRecipientReplacementOutput(replacementOutput)
      const affectedCustomer = replacement?.affectedCustomer ?? null
      const replacementCustomer = replacement?.replacementCustomer ?? null
      const customerLabel = affectedCustomer?.displayName ?? "Falscher Ansprechpartner"
      const replacementName = replacementCustomer?.displayName ?? ""
      const replacementEmail = mutationContactValue(replacementCustomer, ["EMAIL"])
      const replacementPhone = mutationContactValue(replacementCustomer, ["MOBILE", "FIXED_LINE"])
      const needsStep = itemNeedsStep(item, step)
      const resolved = itemResolvedStep(item, step)
      const skipped =
        item.steps.find((entry) => entry.step_key === step.step_key)?.status === "skipped"
      const outcome = rowOutcomes[actionCaseId] ?? null
      const outputError = !replacement
        ? priorOutputError
        : resolved && cleanupOutput.confirmed !== true
          ? cleanupOutputError
          : null
      const instructions = replacement ? sourceInstructions(replacement) : []
      const completed = cleanupOutput.confirmed === true
      const addressLabel =
        stringValue(address.label, address.freeform, property.label) || "Liegenschaft"
      const unitLabel = stringValue(unit.label, unit.hint) || "Einheit"
      const sourceCleanupNote = resolveCustomerStatement(wrongRecipient.wrong_recipient_hint)

      return {
        id: actionCaseId,
        actionCaseId,
        workflowItem: item,
        step: item.steps.find((entry) => entry.step_key === step.step_key) ?? null,
        payload: item.action_case.payload,
        sourceLabel: formatInboxSourceLabel(
          stringValue(presentation.source_label, origin.source, item.action_case.source),
        ),
        sourceSubLabel: stringValue(presentation.source_subline, presentation.source_sub_label),
        addressLabel,
        unitLabel,
        customerLabel,
        customerEmail: mutationContactValue(affectedCustomer, ["EMAIL"]),
        customerPhone: mutationContactValue(affectedCustomer, ["MOBILE", "FIXED_LINE"]),
        customerRole: affectedCustomer?.role ?? "",
        customerContactPoints: mutationContactPointsForTag(affectedCustomer),
        sourceCleanupInstructions: instructions,
        sourceCleanupNote,
        completed,
        needsStep,
        rowOutcome: outcome,
        isOpen: needsStep,
        isResolved: resolved,
        isCompatible: item.compatible,
        disabled:
          outcome?.status === "success" || !needsStep || !item.compatible || Boolean(outputError),
        statusLabel:
          outcome?.status === "failed"
            ? "Prüfung nötig"
            : outputError
              ? "Datenfehler"
              : skipped
                ? "Übersprungen"
                : completed
                  ? "Erledigt"
                  : item.compatible
                    ? "Offen"
                    : "Nicht verfügbar",
        statusDetail: outcome?.error || outcome?.detail || outputError || item.error || null,
        searchText: [
          customerLabel,
          replacementName,
          replacementEmail,
          replacementPhone,
          addressLabel,
          unitLabel,
          sourceCleanupNote,
          ...instructions.flatMap((instruction) => [
            instruction.label,
            instruction.subLabel,
            instruction.copyValue,
          ]),
        ].join(" "),
      }
    })
